/**
 * Shared style config for Basis Theory Elements iframes.
 *
 * BT Elements mount inside cross-origin iframes so they cannot inherit
 * site CSS — we mirror the text styles from `input.html.twig` (font
 * Montserrat, 18px / `text-lg`, a11y-neutral-900 for text, -500 for
 * placeholder) and set `caretColor` explicitly so the iframe doesn't
 * fall back to Safari's red autofill caret.
 *
 * `invalid` overrides text back to base color so partial/invalid card
 * numbers don't render in BT's default pink, and paints the iframe's
 * own border red so the validity cue lands at the field level. The
 * outer wrapper div still renders an error border via Tailwind for
 * legacy fields; on BT inputs the two stack visually identically.
 */
import tailwindConfig from "../../../tailwind.config";

// Read straight from the palette so the values can't drift from the
// Tailwind classes the native inputs use (a11y-neutral-900 for text,
// -500 for placeholder, a11y-red-500 for the invalid border).
const colors = tailwindConfig.theme.extend.colors;
const TEXT_COLOR = colors["a11y-neutral"][900];
const PLACEHOLDER_COLOR = colors["a11y-neutral"][500];
const INVALID_COLOR = colors["a11y-red"][500];

// BT's style object only applies inside the iframe. Font/color set
// here mirror the native inputs; focus border treatment stays on the
// outer wrapper div via Tailwind so BT + legacy fields share the
// same visual vocabulary for focus state. The invalid border is
// painted by the iframe so BT's own validity signal lands directly
// on the field.
export const btElementStyle = {
    // Load Montserrat into the BT iframe. The iframe is cross-origin
    // (api.basistheory.com) so it can't inherit the parent page's
    // fonts — Chrome sometimes picks up the system-installed font,
    // but Safari does not, which shows up as the BT input falling
    // back to its default font. This matches the href in the owner
    // portal's layout <link rel="stylesheet">.
    fonts: [
        'https://fonts.googleapis.com/css?family=Montserrat:300,400,500,600,700&display=swap',
    ],
    base: {
        color: TEXT_COLOR,
        caretColor: TEXT_COLOR,
        fontFamily: '"Montserrat", Helvetica, Arial, sans-serif',
        fontSize: '18px',
        fontWeight: '400',
        '::placeholder': {
            color: PLACEHOLDER_COLOR,
        },
    },
    // BT's built-in `empty`-state CSS overrides `base.caretColor`
    // (its default paints an empty-input caret red as a validity
    // hint). Without this override, focusing an untouched BT field
    // flashes a red caret even though base is set. Keys MUST stay
    // camelCase — BT's server rejects unknown kebab-case keys like
    // `caret-color` with a BasisTheoryValidationError on tokenize.
    empty: {
        color: TEXT_COLOR,
        caretColor: TEXT_COLOR,
    },
    invalid: {
        color: TEXT_COLOR,
        caretColor: TEXT_COLOR,
        borderColor: INVALID_COLOR,
    },
};
